import { useEffect, useState } from 'react'
import { getTenantUrl } from '@/utils/tenant'

type Lang = 'ko' | 'en'

const KNOWN_TENANTS = [
  { id: 'lgcns', name: 'LG CNS', logo: '/lgcns-logo.png' },
  { id: 'samsung', name: 'Samsung', logo: '/samsung-logo.svg' },
  { id: 'demo', name: 'Demo', logo: '/quebon-logo.svg' },
]

const COPY = {
  ko: {
    badge: 'AI 기반 기업 학습 플랫폼',
    heroTitle1: 'Welcome to ',
    heroTitle2: 'AIQ Lab',
    heroDesc: 'AI 기반 기업 교육의 새로운 기준. 학습자 맞춤형 콘텐츠와 실시간 분석으로 조직의 역량 성장을 가속화합니다.',
    features: [
      { title: 'AI 맞춤형 학습', desc: '학습자 수준에 맞는 AI 기반 적응형 학습 경험을 제공합니다.' },
      { title: '실시간 학습 분석', desc: '학습 진행률과 성과를 실시간으로 모니터링하고 인사이트를 제공합니다.' },
      { title: '기업 맞춤 관리', desc: '부서, 클래스, 학습그룹별 유연한 학습 관리 체계를 구축합니다.' },
    ],
    portalTitle: '기업 전용 포털',
    portalDesc: '소속 기업의 포털로 바로 이동하세요.',
    portalHint: '기업 URL을 직접 입력하여 접속할 수도 있습니다: ',
    portalExample: 'https://회사명.aiqlab.tv',
  },
  en: {
    badge: 'AI-Powered Enterprise Learning',
    heroTitle1: 'Welcome to ',
    heroTitle2: 'AIQ Lab',
    heroDesc: 'A new standard in AI-powered corporate training. Accelerate your organization\'s growth with adaptive content and real-time analytics.',
    features: [
      { title: 'Adaptive AI Learning', desc: 'Deliver personalized learning experiences powered by AI, tailored to each learner\'s level.' },
      { title: 'Real-time Analytics', desc: 'Monitor learning progress and performance in real time with actionable insights.' },
      { title: 'Enterprise Management', desc: 'Build flexible learning structures by department, class, and learning group.' },
    ],
    portalTitle: 'Enterprise Portal',
    portalDesc: 'Go directly to your company\'s learning portal.',
    portalHint: 'You can also access your portal by entering the URL directly: ',
    portalExample: 'https://yourcompany.aiqlab.tv',
  },
} as const

const FEATURE_ICONS = [
  <svg key="f1" viewBox="0 0 24 24" fill="none" className="w-7 h-7" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
  </svg>,
  <svg key="f2" viewBox="0 0 24 24" fill="none" className="w-7 h-7" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
  </svg>,
  <svg key="f3" viewBox="0 0 24 24" fill="none" className="w-7 h-7" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
  </svg>,
]

function FloatingOrb({ className }: { className: string }) {
  return (
    <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
  )
}

function LangToggle({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  return (
    <button
      onClick={() => onChange(lang === 'ko' ? 'en' : 'ko')}
      className="relative flex items-center w-[72px] h-8 rounded-full bg-white/[0.06] border border-white/[0.1] hover:border-white/20 transition-colors cursor-pointer select-none"
      aria-label="Toggle language"
    >
      <span
        className={`absolute top-0.5 h-7 w-9 rounded-full bg-gradient-to-r from-[#719FFF]/60 to-[#5AC6C8]/60 transition-all duration-300 ${
          lang === 'ko' ? 'left-0.5' : 'left-[calc(100%-2.375rem)]'
        }`}
      />
      <span className={`relative z-10 flex-1 text-center text-xs font-semibold transition-colors duration-200 ${lang === 'ko' ? 'text-white' : 'text-white/40'}`}>
        한
      </span>
      <span className={`relative z-10 flex-1 text-center text-xs font-semibold transition-colors duration-200 ${lang === 'en' ? 'text-white' : 'text-white/40'}`}>
        EN
      </span>
    </button>
  )
}

export default function BrandPage() {
  const [mounted, setMounted] = useState(false)
  const [lang, setLang] = useState<Lang>(() => {
    if (typeof navigator !== 'undefined' && navigator.language.startsWith('ko')) return 'ko'
    return 'en'
  })

  const t = COPY[lang]

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50)
    return () => clearTimeout(timer)
  }, [])

  const handleTenantClick = (tenantId: string) => {
    window.location.href = getTenantUrl(tenantId)
  }

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-white overflow-hidden relative">
      {/* Background orbs */}
      <FloatingOrb className="w-[600px] h-[600px] bg-[#719FFF] -top-40 -left-40 animate-pulse" />
      <FloatingOrb className="w-[500px] h-[500px] bg-[#5AC6C8] top-1/3 -right-32 animate-pulse [animation-delay:1s]" />
      <FloatingOrb className="w-[400px] h-[400px] bg-[#719FFF] bottom-20 left-1/4 animate-pulse [animation-delay:2s]" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Nav */}
      <nav className="relative z-10 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/aiq-logo.svg" alt="AIQ Lab" className="h-8 brightness-0 invert" />
        </div>
        <div className="flex items-center gap-3">
          <LangToggle lang={lang} onChange={setLang} />
          <a
            href="mailto:aiqedu@quebon.com"
            className="text-sm text-white/60 hover:text-white transition-colors border border-white/10 rounded-full px-5 py-2 hover:border-white/30"
          >
            Contact Us
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section
        className={`relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-16 transition-all duration-1000 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/70 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#5AC6C8] animate-pulse" />
            {t.badge}
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight">
            {t.heroTitle1}
            <span className="bg-gradient-to-r from-[#719FFF] to-[#5AC6C8] bg-clip-text text-transparent">
              {t.heroTitle2}
            </span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-white/50 leading-relaxed max-w-2xl">
            {t.heroDesc}
          </p>
        </div>
      </section>

      {/* Features */}
      <section
        className={`relative z-10 max-w-6xl mx-auto px-6 py-16 transition-all duration-1000 delay-300 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="grid md:grid-cols-3 gap-6">
          {t.features.map((f, i) => (
            <div
              key={i}
              className="group rounded-2xl bg-white/[0.03] border border-white/[0.06] p-7 hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#719FFF]/20 to-[#5AC6C8]/20 flex items-center justify-center text-[#5AC6C8] mb-5 group-hover:scale-110 transition-transform">
                {FEATURE_ICONS[i]}
              </div>
              <h3 className="text-lg font-semibold text-white/90 mb-2">{f.title}</h3>
              <p className="text-sm text-white/40 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tenants Portal */}
      <section
        className={`relative z-10 max-w-6xl mx-auto px-6 py-16 transition-all duration-1000 delay-500 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-8 sm:p-12">
          <h2 className="text-2xl font-bold text-white/90 mb-2">{t.portalTitle}</h2>
          <p className="text-white/40 mb-8">{t.portalDesc}</p>

          <div className="flex flex-wrap gap-4">
            {KNOWN_TENANTS.map((tenant) => (
              <button
                key={tenant.id}
                onClick={() => handleTenantClick(tenant.id)}
                className="flex items-center gap-3 px-6 py-3.5 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] hover:border-white/[0.16] transition-all duration-200 group"
              >
                <img
                  src={tenant.logo}
                  alt={tenant.name}
                  className="h-6 w-auto brightness-0 invert opacity-60 group-hover:opacity-100 transition-opacity"
                />
                <span className="text-sm font-medium text-white/70 group-hover:text-white transition-colors">
                  {tenant.name}
                </span>
                <svg className="w-4 h-4 text-white/30 group-hover:text-white/60 group-hover:translate-x-0.5 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            ))}
          </div>

          <div className="mt-8 pt-6 border-t border-white/[0.06]">
            <p className="text-sm text-white/30">
              {t.portalHint}
              <code className="px-2 py-0.5 rounded bg-white/[0.06] text-[#5AC6C8] font-mono text-xs">
                {t.portalExample}
              </code>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 max-w-6xl mx-auto px-6 py-12 mt-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-white/[0.06] pt-8">
          <div className="flex items-center gap-3">
            <img src="/aiq-logo.svg" alt="AIQ Lab" className="h-6 brightness-0 invert opacity-40" />
            <span className="text-sm text-white/30">&copy; {new Date().getFullYear()} AIQ Lab. All rights reserved.</span>
          </div>
          <div className="flex gap-6 text-sm text-white/30">
            <a href="mailto:aiqedu@quebon.com" className="hover:text-white/60 transition-colors">Contact</a>
            <a href="https://quebon.tv" className="hover:text-white/60 transition-colors" target="_blank" rel="noopener noreferrer">Quebon</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
